import { Search2Icon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { FilterChip } from "../../../common/components/FilterChip";
import {
  type PortalMyBidDto,
  BidV2Status,
  PortalLifecycleStatus,
  PortalEventContextType,
  useGetPortalMyBidsQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../../common/formatting/i18nFormatters";
import { usePortalEventUrls } from "../../sourcing-events/portal/usePortalEventUrls";
import { BidV2StatusTag } from "../sourcing-event/BidV2StatusTag";

const SEARCH_DEBOUNCE_MS = 300;

type BidFilter = BidV2Status | "ALL";

const STATUS_ORDER: BidV2Status[] = [
  BidV2Status.Draft,
  BidV2Status.Submitted,
  BidV2Status.Awarded,
  BidV2Status.Rejected,
];
const STATUS_LABEL_KEY: Record<BidV2Status, string> = {
  [BidV2Status.Draft]: "Draft",
  [BidV2Status.Submitted]: "Submitted",
  [BidV2Status.Awarded]: "Awarded",
  [BidV2Status.Rejected]: "Rejected",
};

const isOpenDraft = (bid: PortalMyBidDto): boolean =>
  bid.status === BidV2Status.Draft && bid.event.lifecycle.status === PortalLifecycleStatus.Open;

const matchesQuery = (bid: PortalMyBidDto, query: string): boolean => {
  if (!query) return true;
  const haystack = [bid.event.title, bid.event.buyerName, bid.event.referenceId ?? ""].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const SupplierBidsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = momentLocaleFor(i18n.language);
  const portalUrls = usePortalEventUrls();
  const { show } = useApiError();

  const { data, isLoading, error } = useGetPortalMyBidsQuery({});
  const bids = useMemo(() => data?.bids ?? [], [data]);

  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [filter, setFilter] = useState<BidFilter>("ALL");

  const searched = useMemo(() => bids.filter((bid) => matchesQuery(bid, query)), [bids, query]);
  const countByStatus = useMemo(() => {
    const counts = new Map<BidV2Status, number>();
    for (const bid of bids) counts.set(bid.status, (counts.get(bid.status) ?? 0) + 1);
    return counts;
  }, [bids]);
  const visible = useMemo(
    () => (filter === "ALL" ? searched : searched.filter((bid) => bid.status === filter)),
    [searched, filter]
  );

  const openBid = (bid: PortalMyBidDto) =>
    navigate(
      bid.event.contextType === PortalEventContextType.Private
        ? portalUrls.privateBid.edit.go(bid.eventId, bid.bidId)
        : portalUrls.bid.edit.go(bid.eventId, bid.bidId).intro,
    );
  const openRow = (bid: PortalMyBidDto) =>
    isOpenDraft(bid) ? openBid(bid) : navigate(portalUrls.view.go(bid.eventId));

  return (
    <>
      <Heading as="h1" size="lg">
        {t("My bids")}
      </Heading>
      <Text color="smMuted" mt="2">
        {t("Track the procurements you are bidding on")}
      </Text>

      <InputGroup size="lg" mt="6" mb="5">
        <InputLeftElement pointerEvents="none" h="full" display="flex" alignItems="center" justifyContent="center">
          <Search2Icon color="smTextDisabled" boxSize="4" />
        </InputLeftElement>
        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t("Search your bids") ?? ""}
          bg="smBackground"
          borderColor="smBorder"
          rounded="xl"
          focusBorderColor="smPrimary"
        />
      </InputGroup>

      {error ? (
        show(error)
      ) : isLoading ? (
        <Flex justify="center" py="20">
          <Spinner color="smPrimary" />
        </Flex>
      ) : bids.length === 0 ? (
        <EmptyCard text={t("You have not started any offers yet")} />
      ) : (
        <>
          <Wrap spacing="2" mb="5">
            <WrapItem>
              <FilterChip active={filter === "ALL"} label={t("All")} onClick={() => setFilter("ALL")} />
            </WrapItem>
            {STATUS_ORDER.filter((status) => countByStatus.get(status)).map((status) => (
              <WrapItem key={status}>
                <FilterChip
                  active={filter === status}
                  label={`${t(STATUS_LABEL_KEY[status])} (${countByStatus.get(status)})`}
                  onClick={() => setFilter(status)}
                />
              </WrapItem>
            ))}
          </Wrap>

          {visible.length === 0 ? (
            <EmptyCard text={t("No offers match your current search")} />
          ) : (
            <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("Procurement")}</Th>
                    <Th>{t("Buyer")}</Th>
                    <Th>{t("Status")}</Th>
                    <Th>{t("Deadline")}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visible.map((bid) => (
                    <Tr key={bid.bidId} cursor="pointer" _hover={{ bg: "smPrimarySofter" }} onClick={() => openRow(bid)}>
                      <Td fontWeight="medium" color="smText">
                        {bid.event.title || t("Untitled")}
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="smMuted">
                          {bid.event.buyerName}
                        </Text>
                      </Td>
                      <Td>
                        <BidV2StatusTag status={bid.status} size="sm" />
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="smMuted">
                          {bid.event.bidSubmissionDeadline ? displayDateWithTime(bid.event.bidSubmissionDeadline, null, locale) : "—"}
                        </Text>
                      </Td>
                      <Td textAlign="right" whiteSpace="nowrap">
                        <Button
                          size="sm"
                          variant="link"
                          colorScheme="teal"
                          onClick={(e) => {
                            e.stopPropagation();
                            openBid(bid);
                          }}
                        >
                          {isOpenDraft(bid) ? t("Continue") : t("View offer")}
                        </Button>
                        <Icon as={FiChevronRight} color="smMuted" ml="3" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}
    </>
  );
};

const EmptyCard = ({ text }: { text: string }) => (
  <Card variant="outline" rounded="xl">
    <CardBody py="20">
      <Flex align="center" justify="center">
        <Text color="smMuted">{text}</Text>
      </Flex>
    </CardBody>
  </Card>
);
